import * as Select from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TemplateCategory } from "@/modules/snapshots/businessReports.types";

export const NO_CATEGORY_VALUE = "__none__";

interface Props {
  categories: TemplateCategory[];
  selectedCategoryId: string;
  onCategoryChange: (value: string) => void;
  isCreatingCategory: boolean;
  newCategoryName: string;
  onNewCategoryNameChange: (name: string) => void;
  onCreateCategory: () => void;
  onCancelCreate: () => void;
  createNewValue: string;
}

export const CategorySelector = ({
  categories,
  selectedCategoryId,
  onCategoryChange,
  isCreatingCategory,
  newCategoryName,
  onNewCategoryNameChange,
  onCreateCategory,
  onCancelCreate,
  createNewValue,
}: Props) => {
  if (isCreatingCategory) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-semibold text-[#344054]">New category</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newCategoryName}
            onChange={e => onNewCategoryNameChange(e.target.value)}
            placeholder="Category name"
            className="h-11 flex-1 rounded-md border border-[#ddd5cb] bg-[#fffdfb] px-3 text-sm outline-none placeholder:text-[#9aa4b2] focus:ring-2 focus:ring-[#d1974f]"
            autoFocus
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                onCreateCategory();
              }
              if (e.key === "Escape") {
                onCancelCreate();
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            onClick={onCreateCategory}
            disabled={!newCategoryName.trim()}
            className="h-11 rounded-md bg-[#07142d] px-4 text-sm font-semibold text-white shadow-none hover:bg-[#111b35]"
          >
            Add
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onCancelCreate}
            className="h-11 rounded-md border-[#ddd5cb] bg-white px-4 text-sm font-semibold text-[#344054] shadow-none hover:bg-[#f7f2ec]"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-semibold text-[#344054]">Category</label>
      <Select.Root value={selectedCategoryId || NO_CATEGORY_VALUE} onValueChange={onCategoryChange}>
        <Select.Trigger
          className="inline-flex h-11 w-full items-center justify-between gap-2 rounded-md border border-[#ddd5cb] bg-[#fffdfb] px-4 text-sm font-medium text-[#344054] outline-none hover:bg-white focus:ring-2 focus:ring-[#d1974f] data-[placeholder]:text-[#9aa4b2]"
          aria-label="Select category"
        >
          <Select.Value placeholder="Select category (optional)" />
          <Select.Icon>
            <ChevronDown className="h-4 w-4 text-[#687386]" />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            className="z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-[#ddd5cb] bg-[#fffdfb] shadow-lg"
          >
            <Select.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-[#fffdfb] text-[#c88b43]">
              <ChevronUp className="h-4 w-4" />
            </Select.ScrollUpButton>

            <Select.Viewport className="p-2">
              <Select.Item
                value={NO_CATEGORY_VALUE}
                className="relative flex cursor-pointer select-none items-center rounded-md px-8 py-2 text-sm text-[#687386] outline-none data-[highlighted]:bg-[#f4eadc] data-[highlighted]:text-[#07142d]"
              >
                <Select.ItemText>No category</Select.ItemText>
                <Select.ItemIndicator className="absolute left-2 inline-flex items-center text-[#c88b43]">
                  <Check className="h-4 w-4" />
                </Select.ItemIndicator>
              </Select.Item>

              <Select.Separator className="my-1 h-px bg-[#ebe5de]" />

              {categories.map(category => (
                <Select.Item
                  key={category.id}
                  value={category.id}
                  className="relative flex cursor-pointer select-none items-center rounded-md px-8 py-2 text-sm text-[#344054] outline-none data-[highlighted]:bg-[#f4eadc] data-[highlighted]:text-[#07142d]"
                >
                  <Select.ItemText>{category.name}</Select.ItemText>
                  <Select.ItemIndicator className="absolute left-2 inline-flex items-center text-[#c88b43]">
                    <Check className="h-4 w-4" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}

              <Select.Separator className="my-1 h-px bg-[#ebe5de]" />

              <Select.Item
                value={createNewValue}
                className="relative flex cursor-pointer select-none items-center rounded-md px-8 py-2 text-sm font-semibold text-[#a56a2a] outline-none data-[highlighted]:bg-[#f4eadc] data-[highlighted]:text-[#07142d]"
              >
                <Select.ItemText>
                  <span className="flex items-center gap-1">
                    <Plus className="h-3 w-3" />
                    Create new category
                  </span>
                </Select.ItemText>
              </Select.Item>
            </Select.Viewport>

            <Select.ScrollDownButton className="flex h-6 cursor-default items-center justify-center bg-[#fffdfb] text-[#c88b43]">
              <ChevronDown className="h-4 w-4" />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
};
